import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  useLoginLanding,
  useLoginLandingOptions,
  useUpdateLoginLanding,
  useUpdateMemberAutoAnchorUploads,
  useUpdateMemberLandingView,
  useUser,
} from '@/hooks';
import { useState } from 'react';
import { toast } from 'sonner';
import { SettingsRow, SettingsRowList, SettingsSection } from './SettingsPage';

const NONE_VALUE = 'none';

export function LoginLandingSection() {
  const { data: memberContext } = useUser();
  const landingQuery = useLoginLanding();
  const updateLandingMutation = useUpdateLoginLanding();
  const updateViewMutation = useUpdateMemberLandingView();
  const updateAutoAnchorMutation = useUpdateMemberAutoAnchorUploads();

  const [configureMemberId, setConfigureMemberId] = useState<number | null>(
    memberContext?.selectedMember.id ?? null
  );

  const landing = landingQuery.data;
  // hidePersonalMembers is a member-SWITCHER display preference and is deliberately
  // not applied here: any of the account's members can be configured.
  const memberRows = landing?.members ?? [];
  const selectedRow = memberRows.find((row) => row.memberId === configureMemberId);
  const optionsQuery = useLoginLandingOptions(configureMemberId);

  const isPending =
    updateLandingMutation.isPending || updateViewMutation.isPending || updateAutoAnchorMutation.isPending;
  const landsHereOnLogin =
    configureMemberId !== null && landing?.landingMemberId === configureMemberId;

  const handleLandOnLoginToggle = (enabled: boolean) => {
    if (configureMemberId === null) {
      return;
    }
    updateLandingMutation.mutate(
      { landingMemberId: enabled ? configureMemberId : null },
      {
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to update the login landing member');
        },
      }
    );
  };

  const handleViewChange = (value: string) => {
    if (configureMemberId === null) {
      return;
    }
    updateViewMutation.mutate(
      { memberId: configureMemberId, filterId: value === NONE_VALUE ? null : Number(value) },
      {
        onError: (error: Error) => {
          toast.error(error.message || "Failed to update the member's landing view");
        },
      }
    );
  };

  const handleAutoAnchorToggle = (enabled: boolean) => {
    if (configureMemberId === null) {
      return;
    }
    updateAutoAnchorMutation.mutate(
      { memberId: configureMemberId, autoAnchorUploads: enabled },
      {
        onError: (error: Error) => {
          toast.error(error.message || "Failed to update the member's upload filing setting");
        },
      }
    );
  };

  const tabs = optionsQuery.data?.items.filter((item) => item.isTab) ?? [];
  const savedFilters = optionsQuery.data?.items.filter((item) => !item.isTab) ?? [];

  return (
    <SettingsSection
      title="Landing"
      description="Choose which member each login lands on, and the view that opens whenever you land on a member, at login or when switching members. Never applied on refresh."
    >
      <SettingsRowList>
        <SettingsRow
          title={<Label htmlFor="landing-configure-member">Configure member</Label>}
          detail="The member whose landing you are setting."
          trailing={
            <Select
              value={configureMemberId !== null ? String(configureMemberId) : ''}
              onValueChange={(value) => setConfigureMemberId(Number(value))}
              disabled={landingQuery.isLoading}
            >
              <SelectTrigger id="landing-configure-member" className="w-56">
                <SelectValue placeholder="Select a member" />
              </SelectTrigger>
              <SelectContent>
                {memberRows.map((row) => (
                  <SelectItem key={row.memberId} value={String(row.memberId)}>
                    {row.hubName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
        <SettingsRow
          title={<Label htmlFor="landing-on-login">Land here on login</Label>}
          detail="Each login starts as this member."
          trailing={
            <Switch
              id="landing-on-login"
              checked={landsHereOnLogin}
              onCheckedChange={handleLandOnLoginToggle}
              disabled={configureMemberId === null || isPending}
            />
          }
        />
        <SettingsRow
          title={<Label htmlFor="landing-view">Landing view</Label>}
          detail="Opens when you log in as or switch to this member."
          trailing={
            <Select
              value={
                selectedRow && selectedRow.landingFilterId !== null
                  ? String(selectedRow.landingFilterId)
                  : NONE_VALUE
              }
              onValueChange={handleViewChange}
              disabled={configureMemberId === null || optionsQuery.isLoading || isPending}
            >
              <SelectTrigger id="landing-view" className="w-56">
                <SelectValue placeholder="None (member's default tab)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>None (member&apos;s default tab)</SelectItem>
                {tabs.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Tabs</SelectLabel>
                    {tabs.map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
                {savedFilters.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Saved filters</SelectLabel>
                    {savedFilters.map((item) => (
                      <SelectItem key={item.id} value={String(item.id)}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          }
        />
        <SettingsRow
          title={<Label htmlFor="auto-anchor-uploads">File my uploads automatically</Label>}
          detail="When on, the platform attaches each file you upload under the project it belongs to."
          trailing={
            <Switch
              id="auto-anchor-uploads"
              checked={selectedRow?.autoAnchorUploads ?? false}
              onCheckedChange={handleAutoAnchorToggle}
              disabled={configureMemberId === null || isPending}
            />
          }
        />
      </SettingsRowList>
    </SettingsSection>
  );
}
